import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, inr } from "../../lib/api";
import { PageHeader, Card, Loading, StatusBadge, inputCls, labelCls, btnSecondary } from "../../components/admin/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { CheckCircle2, XCircle, Clock, ArrowLeft, CheckCheck, X as XIcon, User, CreditCard, AlertTriangle, Trash2, Loader2, ChevronLeft, ChevronRight, Upload, BellRing } from "lucide-react";
import { PaymentNotifications } from "../../components/admin/PaymentNotifications";
import { REG_ORDER_KEY } from "./Registrations";
import { toast } from "sonner";

const REJECT_REASONS = ["Wrong amount", "Wrong UPI", "Duplicate transaction", "Invalid screenshot", "Transaction not found", "Suspicious proof", "Other"];

const istDateTime = (iso) => {
  if (!iso) return { date: "—", time: "—" };
  try {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit", year: "numeric" }),
      time: d.toLocaleTimeString("en-US", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true }),
    };
  } catch { return { date: "—", time: "—" }; }
};

const FORM_LABELS = {
  student_name: "Student Name", dob: "Date of Birth", gender: "Gender", standard: "Standard / Class",
  division: "Division", roll_number: "Roll Number", school_name: "School", parent_name: "Parent / Guardian",
  mobile: "Mobile", email: "Email", address: "Address", city: "City", state: "State",
  emergency_contact: "Emergency Contact", notes: "Notes",
};

export default function RegistrationDetail() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState(REJECT_REASONS[0]);
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [reminding, setReminding] = useState(false);

  const load = () => api.get(`/registrations/${rid}`).then((res) => { setR(res.data); setRefreshKey((k) => k + 1); }).catch(() => setR(false));
  useEffect(() => { load(); }, [rid]); // eslint-disable-line

  const order = useMemo(() => { try { return JSON.parse(localStorage.getItem(REG_ORDER_KEY) || "[]"); } catch { return []; } }, []);
  const navIdx = order.indexOf(rid);
  const prevId = navIdx > 0 ? order[navIdx - 1] : null;
  const nextId = navIdx >= 0 && navIdx < order.length - 1 ? order[navIdx + 1] : null;

  const act = async (action, rsn = "") => {
    setBusy(true);
    try {
      if (r?.payment?.payment_id) await api.post(`/payments/${r.payment.payment_id}/verify`, { action, reason: rsn });
      else await api.post(`/registrations/${rid}/manual-verify`, { action, reason: rsn });
      toast.success(action === "verify" ? "Payment verified — confirmation WhatsApp, email & invoice are being sent" : action === "reject" ? "Payment rejected" : "Marked for review");
      setRejectOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const sendReminder = async () => {
    setBusy(true); setReminding(true);
    try {
      const { data } = await api.post(`/registrations/${rid}/send-reminder`);
      toast.success(`Reminder message sent on WhatsApp to ${data.phone}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false); setReminding(false);
  };

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/registrations/${rid}/admin-upload-proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.this_status === "verified") toast.success(data.fully_paid ? "Screenshot verified — payment auto-approved. WhatsApp, email & invoice are being sent" : `Payment auto-approved. ₹${data.remaining} still remaining`);
      else toast.warning(`Checks did not pass (${(data.flags || []).join(", ") || "review needed"}). Saved for review — you can still Verify Payment manually.`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false); setUploading(false);
  };

  if (r === null) return <Loading />;
  if (r === false) return <Card className="p-10 text-center text-slate-500">Registration not found.</Card>;
  const pay = r.payment;
  const visiblePayments = (r.payments || []).filter((p) => !p.deleted);

  const deletePayment = async () => {
    setBusy(true);
    try {
      await api.delete(`/payments/${delTarget.payment_id}`);
      toast.success("Payment moved to Bin");
      setDelTarget(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const formEntries = Object.keys(FORM_LABELS)
    .filter((k) => ["student_name", "parent_name", "mobile"].includes(k) || (r[k] && String(r[k]).trim()))
    .map((k) => ({ label: FORM_LABELS[k], value: k === "school_name" ? (r.school_name || r.school_name) : r[k] }));
  formEntries.forEach((e) => { if (e.label === "School") e.value = r.school_name; });
  const custom = r.custom_data && Object.keys(r.custom_data).length ? Object.entries(r.custom_data) : [];

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm"><ArrowLeft className="w-4 h-4" /> Back to list</button>
        {navIdx >= 0 && order.length > 1 && (
          <div className="flex items-center gap-2" data-testid="reg-nav">
            <span className="text-xs text-slate-400" data-testid="reg-nav-position">{navIdx + 1} of {order.length}</span>
            <button onClick={() => prevId && navigate(`/admin/registrations/${prevId}`)} disabled={!prevId} data-testid="reg-nav-prev" className="flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed"><ChevronLeft className="w-4 h-4" /> Previous</button>
            <button onClick={() => nextId && navigate(`/admin/registrations/${nextId}`)} disabled={!nextId} data-testid="reg-nav-next" className="flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed">Next <ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      <PageHeader title={r.registration_id} subtitle={`${r.student_name} · ${r.program_name || ""} ${r.level_name || ""} ${r.batch_name || ""}`}>
        <StatusBadge status={r.payment_status} />
        <StatusBadge status={r.registration_status} />
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2"><User className="w-4 h-4 text-blue-600" /> Submitted Form</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Date" value={istDateTime(r.created_at).date} />
              <Field label="Time" value={istDateTime(r.created_at).time} />
              {formEntries.map((e) => <Field key={e.label} label={e.label} value={e.value} mono={e.label === "Mobile"} />)}
              <Field label="Program" value={r.program_name} />
              <Field label="Level / Batch" value={`${r.level_name || "—"} / ${r.batch_name || "—"}`} />
            </div>
            {custom.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Custom Fields</div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {custom.map(([k, v]) => <Field key={k} label={k} value={v} />)}
                </div>
              </div>
            )}
          </Card>

          {visiblePayments.length > 0 && (
            <Card className="p-6">
              <h3 className="font-head font-semibold text-slate-900 mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-600" /> Payment Breakdown <span className="text-xs font-normal text-slate-400">({visiblePayments.length} payment{visiblePayments.length > 1 ? "s" : ""})</span></h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">{["#", "Amount", "Transaction", "Method", "Status", ""].map((h) => <th key={h} className="py-2 pr-3 font-semibold">{h}</th>)}</tr></thead>
                  <tbody>
                    {visiblePayments.map((p, i) => (
                      <tr key={p.payment_id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/payments/${p.payment_id}`)} data-testid="reg-payment-row">
                        <td className="py-2.5 pr-3 text-slate-500">{i + 1}</td>
                        <td className="py-2.5 pr-3 font-medium">{inr(p.detected_amount ?? p.paid_amount ?? 0)}</td>
                        <td className="py-2.5 pr-3 font-mono text-xs">{p.utr || "—"}</td>
                        <td className="py-2.5 pr-3">{p.payment_method || "—"}</td>
                        <td className="py-2.5 pr-3"><StatusBadge status={p.status} /></td>
                        <td className="py-2.5 pr-3 text-right">
                          <button onClick={(e) => { e.stopPropagation(); setDelTarget(p); }} data-testid="payment-delete-btn" className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {r.payment_summary && (
                <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-4 text-sm">
                  <Field label="Required" value={inr(r.payment_summary.required)} />
                  <Field label="Total Paid" value={inr(r.payment_summary.total_paid)} />
                  <Field label="Remaining" value={inr(r.payment_summary.remaining)} />
                  <Field label="Overall Payment Status" value={r.payment_summary.fully_paid ? "Fully Paid" : (r.payment_summary.total_paid > 0 ? "Partial Payment" : "Awaiting Payment")} />
                </div>
              )}
            </Card>
          )}

          {pay && (
            <Card className="p-6">
              <h3 className="font-head font-semibold text-slate-900 mb-4">Verification Checks</h3>
              <div className="space-y-2.5">
                {(pay.checks || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    {c.passed ? <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" /> : <XCircle className="w-5 h-5 text-rose-500 flex-shrink-0" />}
                    <span className={c.passed ? "text-slate-700" : "text-rose-600 font-medium"}>{c.name}</span>
                  </div>
                ))}
              </div>
              {pay.flags?.length > 0 && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <div className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Flags</div>
                  <ul className="text-sm text-amber-700 list-disc pl-5 space-y-0.5">{pay.flags.map((fl, i) => <li key={i}>{fl}</li>)}</ul>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm mt-5 pt-5 border-t border-slate-100">
                <Field label="Payment ID" value={r.display_payment_id || pay.payment_id} mono />
                <Field label="Payment Method" value={pay.payment_method || "—"} />
                <Field label="UTR / Transaction" value={pay.utr} mono />
                <Field label="Expected Amount" value={inr(pay.amount)} />
                <Field label="Detected Amount" value={pay.detected_amount != null ? inr(pay.detected_amount) : "—"} />
                <Field label="Expected UPI" value={pay.expected_upi || "—"} />
                <Field label="Detected UPI" value={pay.detected_upi || "—"} />
                <Field label="Txn Date" value={pay.txn_date || "—"} />
                <Field label="Txn Time" value={pay.txn_time || "—"} />
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6 space-y-3">
            <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-600" /> Actions</h3>
            {!pay && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="no-proof-note">No payment screenshot has been submitted yet. If the parent has paid, you can verify manually — the registration will be completed and the WhatsApp message, email and invoice will be sent automatically.</p>
            )}
            <label className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="admin-upload-proof-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading ? "Reading screenshot…" : "Upload Payment Screenshot"}
              <input type="file" accept="image/*" className="hidden" onChange={uploadProof} disabled={busy} data-testid="admin-upload-proof-input" />
            </label>
            <button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60" onClick={() => act("verify")} disabled={busy} data-testid="verify-payment-btn"><CheckCheck className="w-4 h-4" /> Verify Payment</button>
            {r.payment_status !== "verified" && (
              <button className="w-full bg-amber-50 hover:bg-amber-100 text-amber-800 font-medium py-2.5 rounded-xl flex items-center justify-center gap-2 border border-amber-200 disabled:opacity-60" onClick={sendReminder} disabled={busy} data-testid="send-reminder-btn">
                {reminding ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />} Send Reminder Message
              </button>
            )}
            <button className="w-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium py-2.5 rounded-xl flex items-center justify-center gap-2" onClick={() => act("review")} disabled={busy} data-testid="review-payment-btn"><Clock className="w-4 h-4" /> Mark for Review</button>
            <button className="w-full bg-rose-50 hover:bg-rose-100 text-rose-700 font-medium py-2.5 rounded-xl flex items-center justify-center gap-2 border border-rose-200" onClick={() => setRejectOpen(true)} disabled={busy} data-testid="reject-payment-btn"><XIcon className="w-4 h-4" /> Reject Payment</button>
            {pay?.verified_by && <p className="text-xs text-slate-400 pt-2">Last actioned by {pay.verified_by}</p>}
          </Card>

          <PaymentNotifications reg={r} refreshKey={refreshKey} />
        </div>
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Payment</DialogTitle></DialogHeader>
          <div><label className={labelCls}>Reason</label>
            <select className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason">{REJECT_REASONS.map((x) => <option key={x}>{x}</option>)}</select>
          </div>
          <DialogFooter>
            <button className={btnSecondary} onClick={() => setRejectOpen(false)}>Cancel</button>
            <button className="bg-rose-600 hover:bg-rose-700 text-white font-semibold py-2.5 px-4 rounded-xl" onClick={() => act("reject", reason)} disabled={busy} data-testid="confirm-reject-btn">Confirm Reject</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent data-testid="delete-payment-dialog">
          <DialogHeader><DialogTitle>Delete Payment?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Are you sure you want to delete this payment? The payment will be moved to the Bin and automatically removed permanently after 10 days.</p>
          <DialogFooter>
            <button className={btnSecondary} onClick={() => setDelTarget(null)}>Cancel</button>
            <button className="bg-rose-600 hover:bg-rose-700 text-white font-semibold py-2.5 px-4 rounded-xl flex items-center gap-2 disabled:opacity-60" onClick={deletePayment} disabled={busy} data-testid="confirm-delete-payment-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete Payment</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{label}</div>
      <div className={`text-slate-800 ${mono ? "font-mono text-sm" : ""}`}>{value || "—"}</div>
    </div>
  );
}
